import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { 
  CubeIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const ItemForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    item_name: '',
    description: '',
    category_id: '',
    unit_of_measure_id: '',
    is_recurring: false,
    effective_start_date: new Date().toISOString().split('T')[0],
    effective_end_date: '',
    is_active: true,
    pricing_type: 'standard'
  });

  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchDropdownData();
    if (isEditing) {
      fetchItem();
    }
  }, [id]);

  const fetchDropdownData = async () => {
    try {
      const [categoriesRes, unitsRes] = await Promise.all([
        api.get('/price-book/categories'),
        api.get('/price-book/units')
      ]);
      
      setCategories(categoriesRes.data.filter(cat => cat.is_active));
      setUnits(unitsRes.data.filter(unit => unit.is_active));
    } catch (err) {
      console.error('Error fetching dropdown data:', err);
    }
  };

  const fetchItem = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/price-book/items/${id}`);
      const item = response.data;
      
      setFormData({
        item_name: item.item_name || '',
        description: item.description || '',
        category_id: item.category_id || '',
        unit_of_measure_id: item.unit_of_measure_id || '',
        is_recurring: item.is_recurring || false,
        effective_start_date: item.effective_start_date ? item.effective_start_date.split('T')[0] : '',
        effective_end_date: item.effective_end_date ? item.effective_end_date.split('T')[0] : '',
        is_active: item.is_active !== false,
        pricing_type: item.pricing_type || 'standard'
      });
    } catch (err) {
      console.error('Error fetching item:', err);
      setError('Failed to load item data');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.item_name.trim()) {
      errors.item_name = 'Item name is required';
    }

    if (!formData.category_id) {
      errors.category_id = 'Category is required';
    }

    if (!formData.unit_of_measure_id) {
      errors.unit_of_measure_id = 'Unit of measure is required';
    }

    if (!formData.effective_start_date) {
      errors.effective_start_date = 'Effective start date is required';
    }

    if (formData.effective_end_date && formData.effective_start_date) {
      const startDate = new Date(formData.effective_start_date);
      const endDate = new Date(formData.effective_end_date);
      if (endDate <= startDate) {
        errors.effective_end_date = 'End date must be after start date';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = {
        ...formData,
        category_id: parseInt(formData.category_id),
        unit_of_measure_id: parseInt(formData.unit_of_measure_id),
        effective_end_date: formData.effective_end_date || null
      };

      if (isEditing) {
        await api.put(`/price-book/items/${id}`, submitData);
      } else {
        await api.post('/price-book/items', submitData);
      }

      navigate('/price-book/items');
    } catch (err) {
      console.error('Error saving item:', err);
      setError(err.response?.data?.error || 'Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  if (loading && isEditing) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/price-book/items')}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEditing ? 'Edit Item' : 'Add New Item'}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {isEditing ? 'Update item information' : 'Create a new price book item'}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Item Name */}
            <div className="sm:col-span-2">
              <label htmlFor="item_name" className="block text-sm font-medium text-gray-700">
                Item Name *
              </label>
              <input
                type="text"
                name="item_name"
                id="item_name"
                value={formData.item_name}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.item_name ? 'border-red-300' : ''
                }`}
                placeholder="e.g., Website Development, Consulting Services"
              />
              {validationErrors.item_name && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.item_name}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category_id" className="block text-sm font-medium text-gray-700">
                Category *
              </label>
              <select
                name="category_id"
                id="category_id"
                value={formData.category_id}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.category_id ? 'border-red-300' : ''
                }`}
              >
                <option value="">Select Category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.display_label}
                  </option>
                ))}
              </select>
              {validationErrors.category_id && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.category_id}</p>
              )}
            </div>

            {/* Unit of Measure */}
            <div>
              <label htmlFor="unit_of_measure_id" className="block text-sm font-medium text-gray-700">
                Unit of Measure *
              </label>
              <select
                name="unit_of_measure_id"
                id="unit_of_measure_id"
                value={formData.unit_of_measure_id}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.unit_of_measure_id ? 'border-red-300' : ''
                }`}
              >
                <option value="">Select Unit</option>
                {units.map(unit => (
                  <option key={unit.id} value={unit.id}>
                    {unit.display_label} {unit.abbreviation && `(${unit.abbreviation})`}
                  </option>
                ))}
              </select>
              {validationErrors.unit_of_measure_id && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.unit_of_measure_id}</p>
              )}
            </div>

            {/* Effective Start Date */}
            <div>
              <label htmlFor="effective_start_date" className="block text-sm font-medium text-gray-700">
                Effective Start Date *
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CalendarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  name="effective_start_date"
                  id="effective_start_date"
                  value={formData.effective_start_date}
                  onChange={handleChange}
                  className={`pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                    validationErrors.effective_start_date ? 'border-red-300' : ''
                  }`}
                />
              </div>
              {validationErrors.effective_start_date && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.effective_start_date}</p>
              )}
            </div>

            {/* Effective End Date */}
            <div>
              <label htmlFor="effective_end_date" className="block text-sm font-medium text-gray-700">
                Effective End Date
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CalendarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  name="effective_end_date"
                  id="effective_end_date"
                  value={formData.effective_end_date}
                  onChange={handleChange}
                  className={`pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                    validationErrors.effective_end_date ? 'border-red-300' : ''
                  }`}
                />
              </div>
              {validationErrors.effective_end_date && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.effective_end_date}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Leave blank for ongoing availability
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              name="description"
              id="description"
              rows={3}
              value={formData.description}
              onChange={handleChange}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Optional description of this item..."
            />
          </div>

          {/* Pricing Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Pricing Structure *
            </label>
            <div className="space-y-3">
              <div className="flex items-start">
                <input
                  id="pricing_type_standard"
                  name="pricing_type"
                  type="radio"
                  value="standard"
                  checked={formData.pricing_type === 'standard'}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 mt-1"
                />
                <div className="ml-3">
                  <label htmlFor="pricing_type_standard" className="block text-sm font-medium text-gray-900">
                    Standard Pricing
                  </label>
                  <p className="text-xs text-gray-500">
                    Simple single-price structure. Best for items with fixed pricing.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <input
                  id="pricing_type_tiered"
                  name="pricing_type"
                  type="radio"
                  value="tiered"
                  checked={formData.pricing_type === 'tiered'}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 mt-1"
                />
                <div className="ml-3">
                  <label htmlFor="pricing_type_tiered" className="block text-sm font-medium text-gray-900">
                    Tiered Pricing
                  </label>
                  <p className="text-xs text-gray-500">
                    Multiple pricing tiers based on quantity ranges. Best for volume discounts.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Checkboxes */}
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                id="is_recurring"
                name="is_recurring"
                type="checkbox"
                checked={formData.is_recurring}
                onChange={handleChange}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="is_recurring" className="ml-2 block text-sm text-gray-900">
                Recurring Item
              </label>
              <p className="ml-2 text-xs text-gray-500">
                Check if this item is billed on a recurring basis
              </p>
            </div>

            <div className="flex items-center">
              <input
                id="is_active"
                name="is_active"
                type="checkbox"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
              <p className="ml-2 text-xs text-gray-500">
                Inactive items are hidden from selection lists
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/price-book/items')}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <CubeIcon className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update Item' : 'Create Item'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ItemForm; 